/**
 * Import the extendTheme function and ThemeConfig type from Chakra UI
 * @module @chakra-ui/react
 */
import { extendTheme, type ThemeConfig } from '@chakra-ui/react';
/**
 * Import the StyleFunctionProps type from Chakra UI styled-system
 * @module @chakra-ui/styled-system
 */
import type { StyleFunctionProps } from '@chakra-ui/styled-system';

/**
 * Configuration for the Chakra UI theme
 * @constant {ThemeConfig} config
 */
const config: ThemeConfig = {
  initialColorMode: 'system',
  useSystemColorMode: true,
};

/**
 * Extended theme configuration for Chakra UI
 * @constant {Object} theme
 */
const theme = extendTheme({
  config,
  colors: {
    gray: {
      5: '#FDFDFD',
      25: '#F9FAFB',
      50: '#F8FAFC',
      100: '#F2F4F7',
      125: '#F0F2F5',
      150: '#EBEEF2',
      200: '#E4E7EB',
      300: '#D1D4D8',
      400: '#9CA2A8',
      500: '#6B7075',
      600: '#4B4F54',
      700: '#363A3E',
      800: '#202326', // Very subtle blue tint
      900: '#121416',
    },
    brand: {
      50: '#e6f6ff',
      100: '#b3e0ff',
      200: '#80cbff',
      300: '#4db5ff',
      400: '#1a9fff',
      500: '#0088e6',
      600: '#006bb3',
      700: '#004d80',
      800: '#00304d',
      900: '#00121a',
    },
    navy: {
      50: '#e6f1ff',
      100: '#b3d4ff',
      200: '#80b7ff',
      300: '#4d9aff',
      400: '#1a7dff',
      500: '#0066e6',
      600: '#0052b3',
      700: '#003d80',
      800: '#00284d',
      900: '#00121a',
    },
    dark: {
      bg: '#1a1b1e',
      card: '#25262b',
      hover: '#2c2e33',
      border: '#2e2f34',
      text: '#c1c2c5',
      heading: '#ffffff',
    },
  },
  fonts: {
    heading: 'Inter, system-ui, sans-serif',
    body: 'Inter, system-ui, sans-serif',
  },
  styles: {
    global: (props: StyleFunctionProps) => ({
      'html, body, #root': {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        backgroundColor: props.colorMode === 'dark' ? 'dark.bg' : 'gray.50',
        color: props.colorMode === 'dark' ? 'dark.text' : 'gray.800',
      },
      '.chakra-container': {
        backgroundColor: props.colorMode === 'dark' ? 'dark.card' : 'white',
        borderRadius: 'none',
        boxShadow: props.colorMode === 'dark' ? 'none' : 'sm',
        borderWidth: props.colorMode === 'dark' ? '1px' : '0',
        borderColor: props.colorMode === 'dark' ? 'dark.border' : 'transparent',
      },
      '.datefilter-dropdown': {
        _hover: {
          backgroundColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.200',
        }
      },
      '.statusfilter-dropdown': {
        _hover: {
          backgroundColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.200',
        }
      }
    })
  },
  components: {
    Container: {
      baseStyle: {
        maxWidth: '1200px',
        width: '100%',
        padding: { base: '1rem', sm: '1.5rem', md: '2rem' },
        mx: 'auto',
      }
    },
    Button: {
      baseStyle: {
        fontWeight: '500',
        borderRadius: 'md',
        minHeight: { base: '2.5rem', md: '2.25rem' },
        fontSize: { base: 'sm', md: 'sm' },
        letterSpacing: '0.025em',
        _hover: {
          transform: 'none',
          boxShadow: 'none',
        },
        transition: 'all 0.15s ease-out',
        padding: '0 16px',
      },
      variants: {
        solid: (props: StyleFunctionProps) => ({
          bg: props.colorMode === 'dark' ? '#212227' : 'navy.50',
          color: props.colorMode === 'dark' ? 'white' : 'navy.800',
          borderWidth: '1px',
          borderColor: props.colorMode === 'dark' ? 'navy.300' : 'navy.500',
          boxShadow: `
            inset 0 1px 0 0 ${props.colorMode === 'dark' ? 'navy.500' : 'navy.500'},
            0 2px 4px rgba(0, 0, 0, 0.2)
          `,
          _hover: {
            bg: props.colorMode === 'dark' ? '#292a2f' : 'navy.200',
            borderColor: props.colorMode === 'dark' ? 'navy.400' : 'navy.600',
            boxShadow: `
              inset 0 1px 0 0 ${props.colorMode === 'dark' ? 'navy.400' : 'navy.400'},
              0 4px 6px rgba(0, 0, 0, 0.25)
            `,
            transform: 'translateY(-1px)',
            _disabled: {
              bg: props.colorMode === 'dark' ? 'whiteAlpha.200' : 'blackAlpha.200',
            },
          },
          _active: {
            bg: props.colorMode === 'dark' ? 'navy.800' : 'navy.700',
            borderColor: props.colorMode === 'dark' ? 'navy.600' : 'navy.800',
            boxShadow: `
              inset 0 2px 4px rgba(0, 0, 0, 0.3),
              inset 0 1px 2px rgba(0, 0, 0, 0.2)
            `,
            transform: 'translateY(1px)',
          },
        }),
        outline: (props: StyleFunctionProps) => ({
          bg: props.colorMode === 'dark' ? '#25262B' : 'white',
          borderWidth: '1px',
          borderColor: props.colorMode === 'dark' ? 'navy.600' : 'navy.600',
          color: props.colorMode === 'dark' ? 'navy.100' : 'navy.600',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
          _hover: {
            bg: props.colorMode === 'dark' ? 'navy.800' : 'navy.50',
            borderColor: props.colorMode === 'dark' ? 'navy.500' : 'navy.500',
            color: props.colorMode === 'dark' ? 'navy.100' : 'navy.700',
            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
          },
          _active: {
            bg: props.colorMode === 'dark' ? 'navy.900' : 'navy.100',
            transform: 'translateY(1px)',
          },
        }),
        ghost: (props: StyleFunctionProps) => ({
          color: props.colorMode === 'dark' ? 'navy.200' : 'navy.600',
          _hover: {
            bg: props.colorMode === 'dark' ? 'whiteAlpha.100' : 'navy.50',
          },
          _active: {
            bg: props.colorMode === 'dark' ? 'whiteAlpha.200' : 'navy.100',
          },
        }),
      },
    },
    Input: {
      baseStyle: {
        field: {
          minHeight: { base: '2.75rem', md: '2.5rem' },
          fontSize: { base: 'md', md: 'sm' },
          WebkitTapHighlightColor: 'transparent',
        }
      },
      variants: {
        outline: (props: StyleFunctionProps) => ({
          field: {
            borderRadius: 'md',
            borderColor: props.colorMode === 'dark' ? 'dark.border' : 'inherit',
            bg: props.colorMode === 'dark' ? 'dark.card' : 'white',
            _hover: {
              borderColor: props.colorMode === 'dark' ? 'whiteAlpha.400' : 'gray.300',
            },
            _focus: {
              borderColor: 'brand.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
            },
          },
        }),
      },
    },
    Card: {
      baseStyle: (props: StyleFunctionProps) => ({
        container: {
          borderRadius: 'lg',
          boxShadow: props.colorMode === 'dark' ? 'none' : 'sm',
          backgroundColor: props.colorMode === 'dark' ? 'dark.card' : 'white',
          borderWidth: props.colorMode === 'dark' ? '1px' : '0',
          borderColor: props.colorMode === 'dark' ? 'dark.border' : 'transparent',
        },
      }),
    },
    Heading: {
      baseStyle: (props: StyleFunctionProps) => ({
        color: props.colorMode === 'dark' ? 'dark.heading' : 'gray.800',
        fontWeight: '700',
        paddingBottom: '0.25em',
        lineHeight: '1.4',
      }),
    },
    Link: {
      baseStyle: (props: StyleFunctionProps) => ({
        color: props.colorMode === 'dark' ? 'brand.300' : 'brand.500',
        _hover: {
          color: props.colorMode === 'dark' ? 'brand.200' : 'brand.600',
        },
      }),
    },
    Alert: {
      variants: {
        solid: (props: StyleFunctionProps) => ({
          container: {
            bg: props.colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100',
          },
        }),
      },
    },
  },
  shadows: {
    sm: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    md: '0 3px 6px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12)',
    lg: '0 10px 20px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.10)',
    xl: '0 15px 25px rgba(0,0,0,0.15), 0 5px 10px rgba(0,0,0,0.05)',
  },
  space: {
    '4xl': '56rem',
    '3xl': '48rem',
    '2xl': '40rem',
    xl: '32rem',
    lg: '24rem',
    md: '16rem',
    sm: '8rem',
    xs: '4rem',
  },
  radii: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
  },
  sizes: {
    // Touch target sizes
    touch: {
      min: '44px', // Minimum touch target size
      icon: '24px', // Icon size within touch target
      spacing: '8px', // Space between touch targets
    }
  },
});

/**
 * Default export of the theme configuration
 * @constant {Object}
 */
export default theme;
